import type React from "react";
import { Button } from "components/shadcn/ui/button";
import { Card, CardFooter, CardContent } from "components/shadcn/ui/card";
import { HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "components/shadcn/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "components/shadcn/ui/tabs";
import clsx from "clsx";
import lang from "lang";
import {
  FEATURE_HIGHLIGHT_NAME,
  PRICING_INTERVAL,
  PRICING_INTERVAL_KEY,
  SUBSCRIPTION_DISPLAY_NAME,
  SUBSCRIPTION_PLAN_NAME,
  SubscriptionPlan,
} from "../types";
import { formatService, getCardWidthClass } from "helpers";
import { Badge } from "components/shadcn/ui/badge";
import SvgSearchIconSubscription from "components/common/iconComponents/searchIconSubscription";
import SvgCloseIcon from "components/common/iconComponents/closeIcon";
import { enableSourcePlan } from "components/utils/network/endpoints";
import { useCreateSubscription } from "./useCreateSubscription";
import OnboardingTourModal from "../onboarding-tour/onboardingTourModal";
import {
  renderAdditionalFeatureIcon,
  renderFeatureIcon,
} from "../featureIcons";
import SvgTeamIconSubscription from "components/common/iconComponents/teamIconSubscription";
import ToolTipWrapper from "components/common/helper/tooltipWrapper";
import SvgBinocularIcon from "components/common/iconComponents/binocularIcon";

interface CreateSubscriptionProps {
  open: boolean;
  purchasePlan: (plan: SubscriptionPlan, isFreeTrial: boolean) => void;
  close: () => void;
  handleCalendlyInvite: () => void;
  plansData: SubscriptionPlan[];
  planCancelled?: boolean;
}

const CreateSubscription: React.FC<CreateSubscriptionProps> = (props) => {
  const { payment: paymentCopy } = lang;
  const { open, planCancelled, purchasePlan, handleCalendlyInvite, close } =
    props;

  const {
    activeTab,
    user,
    plans,
    paymentStyles,
    showOnboardingTourModal,
    trialDays,
    setActiveTab,
    setShowOnboardingTourModal,
    handleLogout,
  } = useCreateSubscription(props);

  const addtionalServices = [
    {
      lookupKey: "email-outreach",
      name: "Email Outreach",
      canHighlight: false,
      capacity: null,
    },
    {
      lookupKey: "letter-outreach",
      name: "Letter Outreach",
      canHighlight: false,
      capacity: null,
    },
  ];

  return (
    <Dialog open={open}>
      {showOnboardingTourModal && (
        <div className="w-full h-[85vh]">
          <OnboardingTourModal
            isOpen={showOnboardingTourModal}
            onClose={() => setShowOnboardingTourModal(false)}
          />
        </div>
      )}
      <DialogContent className="w-full max-w-[85vw] h-auto max-h-[90vh] flex flex-col px-1 sm:px-2 md:px-4 lg:px-6">
        <DialogHeader className="mb-2 sm:mb-2">
          {planCancelled && (
            <button className="flex justify-end outline-none" onClick={close}>
              <SvgCloseIcon />
            </button>
          )}
        </DialogHeader>

        <div className="text-sm font-semibold text-gray-600 flex flex-col items-center justify-center">
          <div>{paymentCopy.pricingPlanSubtext1}</div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-3 sm:mt-4 overflow-y-auto max-h-[70vh] sm:max-h-[75vh] md:max-h-none pb-1 sm:pb-2 -mx-2 px-2">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              style={{
                background: plan.isRecommended
                  ? user.isHarbourClub
                    ? paymentStyles.cardBackgroundHarbourRecommended
                    : paymentStyles.cardBackgroundDefaultRecommended
                  : "linear-gradient(0deg, #F9FAFB, #F9FAFB)",
              }}
              className={clsx(
                "rounded-xl border border-gray-300 bg-gray-50 overflow-hidden p-3 sm:p-4 flex flex-col flex-grow",
                getCardWidthClass(plans.length),
                plans.length === 1 ? "mx-auto" : ""
              )}
            >
              <CardContent className="flex flex-col gap-3 p-0 sm:p-2">
                <div className="flex flex-row justify-between">
                  <div className="flex flex-col ">
                    <div className="text-base sm:text-lg font-semibold text-gray-600 flex flex-row gap-4">
                      <span
                        className={
                          user.isHarbourClub
                            ? paymentStyles.gradientHarbour
                            : paymentStyles.gradientDefault
                        }
                      >
                        {plan.displayName}
                      </span>
                      {plan.isRecommended && (
                        <Badge
                          className={clsx(
                            "rounded-full px-3 py-1 text-sm h-[fit-content] font-medium",
                            user.isHarbourClub
                              ? "bg-[#B9E6FE] text-[#182059]" // Harbour Club styles
                              : "bg-pink-50 text-primary-700" // Default styles
                          )}
                        >
                          Most Popular
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-baseline">
                      <span className="text-2xl sm:text-3xl font-semibold text-gray-900">
                        ${plan.price.toLocaleString()}
                      </span>
                      <span
                        className={`text-sm ml-1 ${
                          user.isHarbourClub ? "text-blue-500" : "text-gray-500"
                        }`}
                      >
                        {plan.pricingInterval === PRICING_INTERVAL.MONTH
                          ? "/month"
                          : "/year"}
                      </span>
                    </div>
                  </div>
                  {plan.name === SUBSCRIPTION_PLAN_NAME.BUY_SIDE_SEARCHER ? (
                    <SvgSearchIconSubscription isHarbour={user.isHarbourClub} />
                  ) : plan.name === SUBSCRIPTION_PLAN_NAME.BUY_SIDE_EMERGING ? (
                    <SvgBinocularIcon isHarbour={user.isHarbourClub} />
                  ) : (
                    <SvgTeamIconSubscription isHarbour={user.isHarbourClub} />
                  )}
                </div>

                <div className="flex text-left flex-col gap-2 content-start mt-2">
                  <span className="font-semibold text-gray-600 mb-2">
                    {plan.displayName === SUBSCRIPTION_DISPLAY_NAME.EMERGING
                      ? 'Includes:'
                      : 'Everything in Emerging, Plus:'}
                  </span>

                  {(plan.displayName === SUBSCRIPTION_DISPLAY_NAME.EMERGING
                    ? plan.featureGroups.included
                    : plan.featureGroups.highlight
                  ).map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex text-sm sm:text-base mb-2 flex-row gap-4"
                    >
                      <div>
                        {renderFeatureIcon({
                          lookupKey: feature.lookupKey,
                          isHarbour: user?.isHarbourClub,
                        })}
                      </div>

                      <div className="flex flex-row gap-1 text-left">
                        {feature.capacity !== null && (
                          <span className="font-semibold text-gray-600">
                            {feature.capacity.toLocaleString()}
                          </span>
                        )}

                        <span className="text-gray-600">
                          {feature.highlightName ===
                            FEATURE_HIGHLIGHT_NAME.TEAM_MEMBERS &&
                          feature.capacity === 1
                            ? "Team Member"
                            : feature.highlightName ?? feature.name}
                        </span>

                        {feature.capacity !== null &&
                          feature.highlightName !==
                            FEATURE_HIGHLIGHT_NAME.TEAM_MEMBERS &&
                          plan.pricingInterval && (
                            <span className="text-gray-600">
                              {plan.pricingInterval === PRICING_INTERVAL.MONTH
                                ? "/month"
                                : "/year"}
                            </span>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>

              {plan.displayName !== SUBSCRIPTION_DISPLAY_NAME.EMERGING && (
                <div className="flex flex-col items-center justify-center mt-2 sm:mt-2 gap-4">
                  <Button
                    variant="payment"
                    className="w-full sm:w-[80%] text-white"
                    onClick={
                      planCancelled
                        ? () => purchasePlan(plan, false)
                        : () => purchasePlan(plan, true)
                    }
                  >
                    <span className="text-white font-semibold text-sm">
                      {plan.displayName === SUBSCRIPTION_DISPLAY_NAME.TEAM &&
                      !enableSourcePlan
                        ? paymentCopy.getEarlyAccess
                        : planCancelled
                        ? paymentCopy.subscribeNow
                        : paymentCopy.startTrial.replace(
                            "{{days}}",
                            trialDays ?? "0"
                          )}
                    </span>
                    {!planCancelled && enableSourcePlan && (
                      <ToolTipWrapper
                        content={paymentCopy.trialHelpIconTooltip}
                      >
                        <HelpCircle
                          size={16}
                          className="text-white cursor-pointer"
                        />
                      </ToolTipWrapper>
                    )}
                  </Button>
                  {/* {!planCancelled &&
                  (enableSourcePlan ||
                    plan.displayName !== SUBSCRIPTION_DISPLAY_NAME.TEAM) && (
                    <div className="flex items-left gap-2 flex-col">
                      <div className="flex flex-row gap-2 items-center">
                        <button onClick={() => purchasePlan(plan, false)}>
                          <div
                            className={
                              user.isHarbourClub
                                ? paymentStyles.textHarbour
                                : paymentStyles.textDefault
                            }
                          >
                            {paymentCopy.skiptheTrial}
                          </div>
                        </button>
                      </div>
                    </div>
                  )} */}
                </div>
              )}

              {plan.displayName === SUBSCRIPTION_DISPLAY_NAME.EMERGING && (
                <div className="flex flex-col items-center justify-center mt-2 sm:mt-2 gap-4">
                  <Button
                    variant="payment"
                    className="w-full sm:w-[80%] text-white"
                    onClick={() => purchasePlan(plan, false)}
                  >
                    <span className="text-white font-semibold text-sm">
                      {paymentCopy.activateYourPlan}
                    </span>
                  </Button>
                </div>
              )}

              <CardFooter className="p-0 sm:p-2 pt-4 sm:pt-6 flex-grow">
                <div
                  className={clsx(
                    "w-full",
                    plan.displayName === SUBSCRIPTION_DISPLAY_NAME.EMERGING &&
                      "flex align-top"
                  )}
                >
                  {plan.displayName !== SUBSCRIPTION_DISPLAY_NAME.EMERGING && (
                    <h4
                      className={`text-xs sm:text-sm font-semibold uppercase mb-2 ${
                        user.isHarbourClub ? "text-blue-500" : "text-gray-500"
                      }`}
                    >
                      {paymentCopy.services}
                    </h4>
                  )}

                  {(() => {
                    const isEmerging =
                      plan.displayName === SUBSCRIPTION_DISPLAY_NAME.EMERGING;

                    if (isEmerging) return null;

                    const featuresToShow = [
                      ...addtionalServices,
                      ...plan.featureGroups.included,
                    ];

                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {featuresToShow.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            {renderAdditionalFeatureIcon({
                              featureName: feature.lookupKey,
                              isHarbour: user.isHarbourClub,
                            })}

                            <span className="text-sm sm:text-base font-normal">
                              {feature.canHighlight &&
                              feature.name !== "Team Members"
                                ? feature.name
                                : formatService(feature.name, feature.capacity)}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            setActiveTab(
              value as
                | PRICING_INTERVAL_KEY.MONTHLY
                | PRICING_INTERVAL_KEY.YEARLY
            )
          }
          className="w-full max-w-[90%] min-w-[200px] flex justify-center mx-auto "
        >
          <TabsList
            className="flex flex-col sm:flex-row content-center space-x-4 p-2 bg-[#F9FAFB] rounded-lg"
            isHarbour={user.isHarbourClub}
          >
            {paymentCopy.tabOptions.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                isHarbour={user.isHarbourClub}
                className={clsx(
                  "h-[30px] px-4",
                  activeTab === value
                    ? user.isHarbourClub
                      ? "bg-[#3DAEDF]" // Harbour Club colors
                      : "bg-[#FFF1F3]" // Default colors
                    : user.isHarbourClub
                    ? "text-[#182059]" // Harbour Club text color
                    : "text-gray-600" // Default text color
                )}
                style={{
                  color:
                    activeTab === value
                      ? "var(--primary-700)"
                      : "var(--gray-500)",
                }}
              >
                <div className="flex items-center gap-2">
                  {label}
                  {value === "yearly" && (
                    <Badge
                      className={`rounded-full ${
                        activeTab === "yearly"
                          ? user.isHarbourClub
                            ? "bg-[#3DAEDF] text-[#182059]" // Harbour Club badge colors
                            : "bg-[#FFF1F3] text-primary-700 border border-primary-500" // Default badge colors
                          : user.isHarbourClub
                          ? "bg-[#3DAEDF] text-[#182059]" // Harbour Club non-active badge colors
                          : "bg-[#F9FAFB] text-gray-700" // Default non-active badge colors
                      } border border-[--primary-200] px-3 py-1 text-sm font-medium`}
                    >
                      {"Save > 15%"}
                    </Badge>
                  )}
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-row justify-between cursor-default">
          <div className="flex flex-row items-center gap-2">
            <span className="text-sm text-gray-900">
              {paymentCopy.wantToSeeBizCrunchInAction}
            </span>
            <Button variant="primary" onClick={() => handleCalendlyInvite()}>
              {paymentCopy.bookADemo}
            </Button>
          </div>
          <button
            className="content-end text-md sm:text-end sm:text-base font-semibold text-blue-600 hover:text-blue-800 underline cursor-pointer"
            onClick={() => {
              close();
              handleLogout();
            }}
          >
            {paymentCopy.logoutTextModal}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSubscription;
